import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { AuthUser } from '@/shared/domain'
import { reportError, setUserIdentity } from '@/shared/infra/observability'
import { AuthContext } from '@/shared/ui/hooks'
import { useToast } from '@/shared/ui/providers'
import type { LoadSession } from '../../use-cases/load-session'
import type { SignOut } from '../../use-cases/sign-out'
import type { OnAuthStateChange } from '../../use-cases/on-auth-state-change'
import type { LoadAccessToken } from '../../use-cases/load-access-token'
import type { OnAccessTokenChange } from '../../use-cases/on-access-token-change'

type AuthProviderProps = {
  onLoadSession: LoadSession
  onSignOut: SignOut
  onAuthStateChange: OnAuthStateChange
  onLoadAccessToken: LoadAccessToken
  onAccessTokenChange: OnAccessTokenChange
  children: React.ReactNode
}

const AUTH_RESTORE_TIMEOUT_MS = 5000
const SESSION_RESTORE_TOAST_TITLE = 'Connection lost'
const SESSION_RESTORE_FALLBACK_MESSAGE =
  "We couldn't restore your session. Check your connection and try again."

const SIGN_OUT_LOCAL_STORAGE_KEYS = [
  'cc-selected-org-id',
  'cc-member-context',
  'cc-hunter-mode',
  'pendingClub',
  'cc-impersonation',
  'cc-units',
] as const
const SIGN_OUT_SESSION_STORAGE_KEYS = [
  'register_progress',
  'register_otp_state',
  'createOrgMobileData',
] as const

function clearAllDashboardStorage(): void {
  if (typeof window === 'undefined') return
  for (const key of SIGN_OUT_LOCAL_STORAGE_KEYS) {
    window.localStorage.removeItem(key)
  }
  for (const key of SIGN_OUT_SESSION_STORAGE_KEYS) {
    window.sessionStorage.removeItem(key)
  }
}

export function AuthProvider({
  onLoadSession,
  onSignOut,
  onAuthStateChange,
  onLoadAccessToken,
  onAccessTokenChange,
  children,
}: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [restoreTimedOut, setRestoreTimedOut] = useState(false)
  const { errorWithRetry } = useToast()
  const retryRef = useRef<() => void>(() => {})

  const showRestoreRetryToast = useCallback(
    (err: unknown) => {
      const message = reportError(err, { fallbackMessage: SESSION_RESTORE_FALLBACK_MESSAGE })
      errorWithRetry(SESSION_RESTORE_TOAST_TITLE, message, () => retryRef.current())
    },
    [errorWithRetry],
  )

  const retryRestoreSession = useCallback(() => {
    setIsLoading(true)
    onLoadSession()
      .then((u) => {
        setUser(u)
        setUserIdentity(u?.id ?? null)
        setRestoreTimedOut(false)
      })
      .catch(showRestoreRetryToast)
      .finally(() => {
        setIsLoading(false)
      })
  }, [onLoadSession, showRestoreRetryToast])

  useEffect(() => {
    retryRef.current = retryRestoreSession
  }, [retryRestoreSession])

  useEffect(() => {
    let resolved = false
    const timeoutId = setTimeout(() => {
      if (!resolved) setRestoreTimedOut(true)
    }, AUTH_RESTORE_TIMEOUT_MS)

    onLoadSession()
      .then((u) => {
        resolved = true
        clearTimeout(timeoutId)
        setUser(u)
        setUserIdentity(u?.id ?? null)
        setRestoreTimedOut(false)
      })
      .catch((err) => {
        resolved = true
        clearTimeout(timeoutId)
        showRestoreRetryToast(err)
      })
      .finally(() => {
        setIsLoading(false)
      })

    const unsubscribe = onAuthStateChange((u) => {
      setUserIdentity(u?.id ?? null)
      setUser((prev) => {
        if (u === null && prev === null) return prev
        if (u !== null && prev !== null && u.id === prev.id) return prev
        return u
      })
    })

    onLoadAccessToken()
      .then((t) => setAccessToken(t))
      .catch((err) => {
        reportError(err, { fallbackMessage: SESSION_RESTORE_FALLBACK_MESSAGE })
      })
    const unsubscribeToken = onAccessTokenChange((t) => setAccessToken(t))

    return () => {
      clearTimeout(timeoutId)
      unsubscribe()
      unsubscribeToken()
    }
  }, [
    onLoadSession,
    onAuthStateChange,
    onLoadAccessToken,
    onAccessTokenChange,
    showRestoreRetryToast,
  ])

  const signOut = useCallback(async () => {
    try {
      await onSignOut()
    } finally {
      clearAllDashboardStorage()
      setUser(null)
      setUserIdentity(null)
      setAccessToken(null)
    }
  }, [onSignOut])

  const value = useMemo(
    () => ({ user, accessToken, isLoading, restoreTimedOut, signOut }),
    [user, accessToken, isLoading, restoreTimedOut, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
